import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Loading from 'components/Loading/index';
import Util from 'utility';
import api from 'api';
import { __ } from 'locale';
import { showMessage, closeModal, setLectureTaskCompleteSaved } from 'actions/index';

const isSk = () => Util.getLang() === 'sk';

function DialogSaveLectureTask({ defaultTitle, afterClose }) {
    const data = useSelector(state => state);
    const dispatch = useDispatch();


    let dayNames = {
        "Den 1": "Pondělí", "Den 2": "Úterý", "Den 3": "Středa", "Den 4": "Čtvrtek",
        "Den 5": "Pátek", "Den 6": "Sobota", "Den 7": "Neděle",
    };

    if (isSk()) {
        dayNames = {
            "Den 1": "Pondelok", "Den 2": "Utorok", "Den 3": "Streda", "Den 4": "Štvrtok",
            "Den 5": "Piatok", "Den 6": "Sobota", "Den 7": "Nedeľa",
        };
    }

    if (Util.isCurrentTaskSeminarWork()) {
        defaultTitle = dayNames[defaultTitle] || defaultTitle;
    }


    const initialTitle = typeof data.lectureTaskData.lastLoadedTitle === 'string'
        ? data.lectureTaskData.lastLoadedTitle
        : defaultTitle;

    const [title, setTitle] = useState(initialTitle);
    const [tasks, setTasks] = useState([]);
    const [overwriteId, setOverwriteId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api('lecturetask/list/' + data.appModeData.lectureId).then(result => {
            setTasks(result.data.tasks);
            setLoading(false);
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const doSave = (oid) => {
        const isComplete = data.lectureTaskComplete;
        let msg;

        if (!oid) {
            if (title.trim() === '') {
                alert(isSk() ? 'Zadajte prosím názov uloženia' : 'Zadejte prosím název uložení');
                return;
            }
            const titleIsUnique = !tasks.some(task => task.title === title.trim());
            if (!titleIsUnique) {
                alert(isSk()
                    ? 'Uloženie s týmto názvom už existuje. Zvoľte iný názov, alebo nahraďte existujúce uloženie.'
                    : 'Uložení s tímto názvem již existuje. Zvolte jiný název, nebo nahraďte existující uložení.');
                return;
            }
            msg = isSk() ? 'Postup bol uložený.' : 'Postup byl uložen.';
        } else {
            msg = isSk() ? 'Uloženie bolo nahradené.' : 'Uložení bylo nahrazeno.';
        }

        setSaving(true);
        const saveData = JSON.stringify({
            lecture_id: data.appModeData.lectureId,
            title: JSON.stringify(oid ? tasks.find(t => t.id === oid).title : title.trim()),
            comment: JSON.stringify(''),
            meals: JSON.stringify(data.meals),
            groups: JSON.stringify(data.foodGroups),
            foods: JSON.stringify(data.foods),
            modifications: JSON.stringify(data.modifications),
            trainings: JSON.stringify(data.trainings),
            overwriteId: JSON.stringify(oid || 0),
            isComplete: JSON.stringify(isComplete)
        });

        api('save/lecturetask', { post: true, data: saveData }).then(() => {
            dispatch(closeModal());
            dispatch(showMessage({ show: true, type: 'success', message: msg }));
            dispatch(setLectureTaskCompleteSaved(isComplete));
            if (typeof afterClose === 'function') afterClose();
        });
    };

    if (loading) return <Loading />;

    const sk = isSk();

    const existingList = tasks.map(task => {
        const isConfirming = overwriteId === task.id;
        const isCurrentTask = task.lecture_id === data.appModeData.lectureId;

        return (
            <div
                key={task.id}
                className={`rounded-lg border px-4 py-3 mb-2 ${isConfirming ? 'border-orange-400 bg-orange-50' : 'border-[#ddd] bg-[#f7f7f7]'}`}
            >
                <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-[0.95em] text-[#222]">{task.title}</span>
                            {isCurrentTask && (
                                <span className="text-[0.72em] font-semibold bg-blue-100 text-blue-700 px-2 py-[2px] rounded-full whitespace-nowrap">
                                    {sk ? 'aktuálna úloha' : 'aktuální úkol'}
                                </span>
                            )}
                        </div>
                        <p className="text-[0.8em] text-[#777] mt-[2px] mb-0">{task.course_title} / {task.lecture_title}</p>
                    </div>
                    {!isConfirming && (
                        <button
                            className="shrink-0 text-[0.8em] border border-[#bbb] bg-white text-[#555] hover:bg-[#f0f0f0] rounded px-3 py-[5px] cursor-pointer"
                            onClick={() => setOverwriteId(task.id)}
                            disabled={saving}
                        >
                            {sk ? 'Nahradiť' : 'Nahradit'}
                        </button>
                    )}
                </div>
                {isConfirming && (
                    <div className="mt-3 pt-3 border-t border-orange-300 flex items-center gap-2 flex-wrap">
                        <span className="text-[0.85em] text-orange-700 font-medium flex-1">
                            ⚠️ {sk
                                ? 'Naozaj nahradiť toto uloženie aktuálnym riešením?'
                                : 'Opravdu nahradit toto uložení aktuálním řešením?'}
                        </span>
                        <button
                            className="text-[0.85em] bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded px-3 py-[5px] cursor-pointer border-0"
                            onClick={() => doSave(task.id)}
                            disabled={saving}
                        >
                            {sk ? 'Áno, nahradiť' : 'Ano, nahradit'}
                        </button>
                        <button
                            className="text-[0.85em] bg-[#eee] hover:bg-[#ddd] text-[#444] rounded px-3 py-[5px] cursor-pointer border border-[#ccc]"
                            onClick={() => setOverwriteId(null)}
                            disabled={saving}
                        >
                            {sk ? 'Zrušiť' : 'Zrušit'}
                        </button>
                    </div>
                )}
            </div>
        );
    });

    return (
        <div>
            {/* Uložit jako nové */}
            <div className="rounded-lg border-2 border-[#26AA52] bg-green-50 px-4 py-4 mb-5">
                <p className="text-[0.8em] font-semibold text-green-800 uppercase tracking-wide mb-2 mt-0">
                    {sk ? 'Uložiť ako nové' : 'Uložit jako nové'}
                </p>
                <div className="flex items-center gap-2">
                    <input
                        className="text-[0.9em] py-[0.5em] px-[0.75em] rounded border border-[#bbb] font-semibold flex-1 min-w-0 bg-white"
                        type="text"
                        name="title"
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                        placeholder={sk ? 'Názov uloženia...' : 'Název uložení...'}
                        onKeyDown={e => e.key === 'Enter' && doSave(null)}
                        disabled={saving}
                    />
                    <button
                        className="shrink-0 font-bold text-[0.9em] bg-[#26AA52] hover:bg-[#2bbf5c] cursor-pointer text-white py-[0.5em] px-[1em] rounded border-0 disabled:opacity-50"
                        type="button"
                        onClick={() => doSave(null)}
                        disabled={saving}
                    >
                        {sk ? 'Uložiť' : 'Uložit'}
                    </button>
                </div>
            </div>

            {/* Existující uložení */}
            {tasks.length > 0 && (
                <>
                    <p className="text-[0.8em] font-semibold text-[#888] uppercase tracking-wide mb-2 mt-0">
                        {sk ? 'alebo nahradiť existujúce uloženie' : 'nebo nahradit existující uložení'}
                    </p>
                    {existingList}
                </>
            )}
        </div>
    );
}

export default DialogSaveLectureTask;
